import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { CATEGORY_ALIAS } from "../../../types/talk";

import { getServiceNameWithNewLine } from "../../../libs/replace";
import { onLikePost } from "../../../libs/talk";

import { useUserContext } from "../../../contexts/userContext";
import { useSandiTalk } from "../../../contexts/boardContext";

import {
  UserServiceResponse,
  searchService,
} from "../../../services/userServiceServices";
import {
  PageableRequest,
  TalkListItem,
  searchLargeCategory,
} from "../../../services/talkServices";

import useNavigateService from "../../../hooks/useNavigateService";
import useInfiniteScroll, {
  INFINITE_CODE,
} from "../../../hooks/useInfiniteScroll";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import { TalkSearchInput } from "../../../components/SearchHeader";
import HorizontalRule from "../../../components/Rule/Horizontal";
import PostList from "../../../components/Talk/PostList";
import ServiceBadge from "../../../components/Badge/Service";

type Props = {};

export default function ViewSearch(props: Props) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchWord, setSearchWord] = useState(state?.searchQuery ?? "");

  const tabList = [
    { title: "서비스", key: "service" },
    { title: "샌디톡", key: "sanditalk" },
  ];

  const [selectedTab, setSelectedTab] = useState<string>(tabList[0].key);
  const [serviceList, setServiceList] = useState<UserServiceResponse[]>([]);
  const [postList, setPostList] = useState<TalkListItem[]>([]);
  const [navigateService, navigateServiceComponent] = useNavigateService({});
  const [user] = useUserContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [talk] = useSandiTalk();
  const [isLoading, setIsLoading] = useState(true);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const loadSearch = async ({ page = 1, pageSize = 10 }: PageableRequest) => {
    let returnValue = INFINITE_CODE.ERROR;

    setIsLoading(true);
    if (selectedTab === "service") {
      const res = await searchService({ searchWord, page, pageSize });

      if (res.status === HttpStatusCode.Ok) {
        if (page === 1) {
          setServiceList(res.data.items);
        } else {
          setServiceList((prev) => [...prev, ...res.data.items]);
        }
        returnValue = res.data.items.length;
      }
    } else {
      const res = await searchLargeCategory({
        category: CATEGORY_ALIAS.TALK,
        searchWord,
        sort: talk.sort,
        page,
        pageSize,
      });

      if (res.status === HttpStatusCode.Ok) {
        if (page === 1) {
          setPostList(res.data.items);
        } else {
          setPostList((prev) => [...prev, ...res.data.items]);
        }
        returnValue = res.data.items.length;
      }
    }
    setIsLoading(false);
    return returnValue;
  };

  const [, { resetScroll }] = useInfiniteScroll({
    callback: async (args) => {
      return loadSearch(args);
    },
    isLoading: isLoading,
  });

  useEffect(() => {
    resetScroll();
    loadSearch({ page: 1, pageSize: 10 });
  }, [selectedTab, searchWord, talk.sort]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef.current]);

  return (
    <div className="flex flex-col gap-[30px]">
      {loadingComponent}
      <StickyHeader
        onBackClick={() => navigate(-1)}
        className="px-4 w-full py-0"
        selectContent={
          <TalkSearchInput
            onClick={setSearchWord}
            className="rounded"
            ref={inputRef}
            autoComplete="off"
          />
        }
      ></StickyHeader>

      <div className="flex flex-col gap-4">
        <div>
          <div className="px-5 grid grid-cols-2">
            {tabList.map((tab) => (
              <div
                key={tab.key}
                className={clsx([
                  "flex justify-center pb-3",
                  {
                    "border-b-[3px] border-brand-primary-magenta font-semibold":
                      selectedTab === tab.key,
                  },
                ])}
                onClick={() => setSelectedTab(tab.key)}
              >
                {tab.title}
              </div>
            ))}
          </div>
          <HorizontalRule margin="0" />
        </div>

        {selectedTab === "service" && (
          <div className="px-5">
            {serviceList.map((service) => (
              <>
                <div
                  className="py-1.5"
                  onClick={() => navigateService(service)}
                >
                  <div className="py-2.5 flex gap-2.5 items-center">
                    <div className="h-[56px] w-[56px] flex items-center justify-center rounded-full bg-brand-weather-badge-background">
                      {service.fileInfo?.items.length > 0 && (
                        <img
                          src={service.fileInfo.items?.[0]?.url}
                          alt={service.serviceName}
                          className={clsx([
                            "w-[36px] h-[36px] object-contain",
                            {
                              "opacity-60": !service.serviceId,
                            },
                          ])}
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      {getServiceNameWithNewLine(service.serviceName)}
                    </div>

                    <ServiceBadge service={service} />
                  </div>
                </div>
                <HorizontalRule margin="0" />
              </>
            ))}
          </div>
        )}
        {selectedTab === "sanditalk" && (
          <PostList
            postList={postList}
            onClickLike={(postId) =>
              setPostList((prev) =>
                prev.map((post) =>
                  post.lifePostId === postId ? onLikePost(post, user) : post
                )
              )
            }
          />
        )}
      </div>

      {navigateServiceComponent}
    </div>
  );
}
